import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, FloatButton, Table, message } from 'antd';
import { HomeOutlined, UserAddOutlined } from '@ant-design/icons';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import PropTypes from 'prop-types';
import AddResidentModal from './addResident';
import SEO from './SEO';
import {
  colourPalette,
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db } from '../firebase';
import './style.scss';

export default function Residents({ site }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'record')}`,
    total: data.length,
  };

  // destructure imports
  const { cplNormal } = colourPalette;
  const { timeoutInfo, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeInfo, typeSuccess, typeWarning } = notificationType;
  const { id } = site;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchResidents = useCallback(
    (siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      const residents = [];
      const residentRef = collection(db, 'accounts');
      const q = query(
        residentRef,
        where('siteId', '==', siteId),
        orderBy('fullname', 'asc'),
      );

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const resident = {};
            const dict = doc.data();

            resident.accessLevel = dict.accessLevel;
            resident.accountType = dict.accountType;
            resident.blockNumber = dict.blockNumber;
            resident.contactNumber = dict.contactNumber;
            resident.email = dict.email;
            resident.fullname = dict.fullname;
            resident.residentID = doc.id;
            resident.status = dict.status;
            resident.unitNumber = dict.unitNumber;

            residents.push(resident);
          });

          if (residents?.length) {
            openMessage(
              `fetching ${residents.length} ${pluralise(residents.length, 'account')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(residents);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
    ],
  );

  const columns = [
    {
      title: 'as',
      dataIndex: 'fullname',
      width: 100,
    },
    {
      title: 'fff',
      dataIndex: 'email',
      width: 120,
    },
  ];

  useEffect(() => fetchResidents(id), [fetchResidents, id]);

  return (
    <>
      <SEO
        description="Manage resident stuff"
        name="qiwei"
        title="Residents"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Residents</span>,
            },
          ]}
        />
      </div>
      {contextHolder}
      <FloatButton
        icon={<UserAddOutlined />}
        onClick={() => setShowAdd(true)}
        shape="square"
        style={{ insetInlineEnd: 48 }}
        tooltip={{
          arrow: false,
          color: cplNormal,
          placement: 'left',
          title: 'Add New Resident',
        }}
        type="primary"
      />
      <AddResidentModal
        message={openMessage}
        reload={fetchResidents}
        showForm={setShowAdd}
        siteName={site.name}
        visible={showAdd}
      />
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="residentID"
        scroll={{ x: 200, y: scrollHeight }}
      />
    </>
  );
}

Residents.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
